// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';

// service imports
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';

// model imports
import { APCreditApplicationSourceTransactionResultDto } from '../../../_models/accounts-payable/apply-debit/ap-credit-application-source-transaction-result-dto';
import { APCreditApplicationTargetParametersDto } from '../../../_models/accounts-payable/apply-debit/ap-credit-application-target-parameters-dto';
import { APCreditApplicationTargetTransactionResultDto } from '../../../_models/accounts-payable/apply-debit/ap-credit-application-target-transaction-result-dto';
import { APCreditApplicationTransactionItemParametersDto } from '../../../_models/accounts-payable/apply-debit/ap-credit-application-transaction-item-parameters-dto';
import { APCreditApplicationTransactionItemResultDto } from '../../../_models/accounts-payable/apply-debit/ap-credit-application-transaction-item-result-dto';
import { AppliedAmountEditorLineItemInfo } from '../../../_models/accounts-payable/apply-debit/applied-amount-editor-line-item-info';

@Component({
    selector: 'app-apply-debit-line-item-amount-editor',
    templateUrl: './apply-debit-line-item-amount-editor.component.html',
    styleUrls: ['./apply-debit-line-item-amount-editor.component.scss']
})
export class ApplyDebitLineItemAmountEditorComponent implements OnInit {
    @ViewChild('applyDebitLineItemEditorPopup', { static: false }) applyDebitLineItemEditorPopup: DxPopupComponent;
    @ViewChild('applyDebitMessageBox', { static: false }) applyDebitMessageBox: MessageBoxComponent;

    @Input() sourceTransactions: APCreditApplicationSourceTransactionResultDto;
    @Input() targetTransactions: Array<APCreditApplicationTargetTransactionResultDto>;
    @Input() targetTransactionId: number;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onSave = new EventEmitter<APCreditApplicationTargetParametersDto>();

    visible = false;

    currencyFormat: string;

    sourceLineInfo: Array<AppliedAmountEditorLineItemInfo> = [];
    sourceLineItems: Array<APCreditApplicationTransactionItemResultDto> = [];
    targetLineItems: Array<APCreditApplicationTransactionItemResultDto> = [];

    transactionItemParams: Array<APCreditApplicationTransactionItemParametersDto> = [];

    iconSave = IconConstants.SAVE;
    iconCancel = IconConstants.CANCEL;

    constructor(private readonly precisionService: PrecisionService) {}

    ngOnInit(): void {
        this.currencyFormat = this.precisionService.getCurrencyFormat();

        this.show();
    }

    show(): void {
        this.visible = true;
        this.prepareSourceLineItems();
    }

    onShown(): void {
        DxWidgetUtils.instantRepaint(this.applyDebitLineItemEditorPopup);
    }

    onHidden(): void {
        this.closeLineItemEditor();
    }

    onCancelClick(): void {
        this.closeLineItemEditor();
    }

    onSaveClick(): void {
        this.prepareTargetItems();
    }

    onRowUpdated(event: any): void {
        const selectedDataRow = event.data;
        event.component.selectRows([event.key], true);

        const resultStatus = this.checkValidity(selectedDataRow);

        if (Utils.isTrue(resultStatus.isValid)) {
            this.sourceLineInfo.forEach((record) => {
                const transactionItem = this.transactionItemParams.find((d) =>
                    Utils.equals(d.sourceItemId, record.itemId)
                );
                if (Utils.notNullAndDefined(transactionItem)) {
                    record.appliedAmount = transactionItem.applyAmount;
                    record.remainingUnapplied = record.amount - transactionItem.applyAmount;
                }
            });
        } else {
            this.applyDebitMessageBox.showErrorMessage('Error', resultStatus.message);
        }
    }

    getItemLabel(item: any): string {
        return `Source Line Item # ${item.rowIndex + 1}`;
    }

    private checkValidity(selectedDataRow: any): any {
        const resultStatus = { isValid: true, message: null };

        this.targetTransactions.forEach((transaction) => {
            transaction.lineItems.forEach((lineItem) => {
                if (Utils.equals(lineItem.itemId, selectedDataRow.itemId)) {
                    const transactionItem = this.transactionItemParams.find((d) =>
                        Utils.equals(d.targetItemId, lineItem.itemId)
                    );
                    if (Utils.notNullAndDefined(transactionItem)) {
                        let amountToBeApplied = 0;
                        if (Utils.equals(transactionItem.sourceItemId, selectedDataRow.sourceItemId)) {
                            amountToBeApplied = selectedDataRow.appliedAmount;
                        } else {
                            amountToBeApplied = transactionItem.applyAmount + selectedDataRow.appliedAmount;
                        }

                        if (lineItem.openBalance >= amountToBeApplied) {
                            if (Utils.equals(lineItem['sourceItemId'], selectedDataRow.sourceItemId)) {
                                this.transactionItemParams.forEach((item) => {
                                    if (
                                        Utils.equals(item.targetItemId, lineItem.itemId) &&
                                        Utils.equals(item.sourceItemId, lineItem['sourceItemId'])
                                    ) {
                                        item.applyAmount = amountToBeApplied;
                                    }
                                });
                            } else {
                                const obj: APCreditApplicationTransactionItemParametersDto =
                                    new APCreditApplicationTransactionItemParametersDto();
                                obj.sourceItemId = selectedDataRow.sourceItemId;
                                obj.targetItemId = selectedDataRow.itemId;
                                obj.applyAmount = selectedDataRow.appliedAmount;

                                this.transactionItemParams.push(obj);
                            }
                        } else {
                            resultStatus.isValid = false;
                            resultStatus.message = 'The Apply Amount exceeds the Line Item Open Balance.';
                        }
                    } else {
                        if (lineItem.openBalance >= selectedDataRow.appliedAmount) {
                            const obj: APCreditApplicationTransactionItemParametersDto =
                                new APCreditApplicationTransactionItemParametersDto();
                            obj.sourceItemId = selectedDataRow.sourceItemId;
                            obj.targetItemId = selectedDataRow.itemId;
                            obj.applyAmount = selectedDataRow.appliedAmount;

                            this.transactionItemParams.push(obj);
                        } else {
                            resultStatus.isValid = false;
                            resultStatus.message = 'The Apply Amount exceeds the Line Item Open Balance.';
                        }
                    }
                }
            });
        });

        return resultStatus;
    }

    private prepareSourceLineItems(): void {
        this.sourceLineInfo = [];

        const sourceLineItems: Array<APCreditApplicationTransactionItemResultDto> = [];
        this.sourceTransactions.lineItems.forEach((lineItem) => {
            const obj: AppliedAmountEditorLineItemInfo = new AppliedAmountEditorLineItemInfo();
            obj.line = this.sourceTransactions.lineItems.indexOf(lineItem) + 1;
            obj.amount = lineItem.amount;
            obj.appliedAmount = 0;
            obj.itemId = lineItem.itemId;
            obj.remainingUnapplied = lineItem.amount;

            this.sourceLineInfo.push(obj);

            let lineObj: APCreditApplicationTransactionItemResultDto =
                new APCreditApplicationTransactionItemResultDto();
            lineObj = Object.assign(lineObj, lineItem);
            lineObj['targetItems'] = this.prepareTargetLineItems(lineObj.itemId);
            sourceLineItems.push(lineObj);
        });

        this.sourceLineItems = sourceLineItems;
    }

    private prepareTargetLineItems(sourceItemId: number): Array<APCreditApplicationTransactionItemResultDto> {
        const targetLineItems: Array<APCreditApplicationTransactionItemResultDto> = [];
        this.targetTransactions.forEach((transaction) => {
            transaction.lineItems.forEach((targetItem) => {
                let obj: APCreditApplicationTransactionItemResultDto =
                    new APCreditApplicationTransactionItemResultDto();
                obj = Object.assign(obj, targetItem);
                obj['sourceItemId'] = sourceItemId;
                targetLineItems.push(obj);
            });
        });

        return targetLineItems;
    }

    private prepareTargetItems(): void {
        const targetParam: APCreditApplicationTargetParametersDto = new APCreditApplicationTargetParametersDto();
        targetParam.targetId = this.targetTransactionId;
        targetParam.items = this.transactionItemParams;
        this.onSave.emit(targetParam);
        this.closeLineItemEditor();
    }

    private closeLineItemEditor(): void {
        this.visible = false;
        this.onClose.emit();
    }
}
